import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { CalendarCheck, Gem, Ruler, Scissors } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";

const IMG = "https://static.prod-images.emergentagent.com/jobs/152026f0-1513-45ae-b838-38d02f56f416/images";
const ATELIER = `${IMG}/ebcdf2e0e87505f5d26eb1d7cd400ced0705a69f15b22d05a6d88245f869bc4b.jpeg`;
const FITTING = `${IMG}/169f764d30d9e7b932aee909c3104469eb5bb7b22e683710859eb2f1b0fb88fd.jpeg`;

export default function LaMaison() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", service: "Sur-mesure", preferred_date: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const VALUES = [
    { icon: Ruler, title: t("maison.v1t"), text: t("maison.v1d") },
    { icon: Scissors, title: t("maison.v2t"), text: t("maison.v2d") },
    { icon: Gem, title: t("maison.v3t"), text: t("maison.v3d") },
  ];

  const SERVICES = [
    { v: "Sur-mesure", labelKey: "maison.svc1" },
    { v: "Retouches", labelKey: "maison.svc2" },
    { v: "Conseil d'image", labelKey: "maison.svc3" },
    { v: "Grandes occasions", labelKey: "maison.svc4" },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/appointments", form);
      toast.success(t("maison.ok", { ref: res.data.ref }), { description: t("maison.okDesc") });
      setForm({ full_name: "", email: "", phone: "", service: "Sur-mesure", preferred_date: "", message: "" });
    } catch {
      toast.error(t("maison.err"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="la-maison-page">
      <section className="relative flex min-h-[70vh] items-end overflow-hidden">
        <img src={ATELIER} alt="L'atelier B.KOSS" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-noir via-noir/60 to-noir/30" />
        <div className="relative mx-auto w-full max-w-[1500px] px-6 pb-20 pt-44 lg:px-12">
          <Reveal>
            <p className="eyebrow">{t("maison.heroEyebrow")}</p>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl uppercase leading-tight tracking-tight text-ivoire sm:text-5xl lg:text-6xl">
              {t("maison.heroPre")} <span className="italic normal-case text-or">{t("maison.heroIt")}</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto grid max-w-[1500px] gap-14 px-6 py-24 lg:grid-cols-2 lg:px-12 lg:py-32">
        <Reveal>
          <h2 className="font-serif text-3xl uppercase tracking-tight text-ivoire sm:text-4xl">
            {t("maison.storyTitlePre")} <span className="italic normal-case text-or">{t("maison.storyTitleIt")}</span>
          </h2>
          <div className="mt-8 space-y-6 leading-relaxed text-argile">
            <p>{t("maison.story1")}</p>
            <p>{t("maison.story2")}</p>
            <p className="font-serif text-xl italic text-ivoire">{t("maison.quote")}</p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="gold-frame frame-inner relative overflow-hidden border border-ligne">
            <img src={FITTING} alt="Séance de sur-mesure" className="aspect-[4/5] w-full object-cover" />
          </div>
        </Reveal>
      </section>

      <section className="border-y border-ligne bg-card2 noise">
        <div className="mx-auto max-w-[1500px] px-6 py-24 lg:px-12">
          <Reveal>
            <p className="eyebrow">{t("maison.valEyebrow")}</p>
            <h2 className="mt-4 font-serif text-3xl uppercase tracking-tight text-ivoire sm:text-4xl">{t("maison.valPre")} <span className="italic normal-case text-or">{t("maison.valIt")}</span></h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="gold-frame h-full border border-ligne bg-elevated p-8">
                  <v.icon size={26} strokeWidth={1.2} className="text-or" />
                  <h3 className="mt-6 font-serif text-xl text-ivoire">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-argile">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="rendez-vous" className="mx-auto grid max-w-[1500px] items-start gap-14 px-6 py-24 lg:grid-cols-2 lg:px-12 lg:py-32">
        <Reveal>
          <p className="eyebrow">{t("maison.rdvEyebrow")}</p>
          <h2 className="mt-5 font-serif text-3xl uppercase tracking-tight text-ivoire sm:text-4xl lg:text-5xl">
            {t("maison.rdvPre")} <span className="italic normal-case text-or">{t("maison.rdvIt")}</span>
          </h2>
          <p className="mt-8 max-w-md leading-relaxed text-argile">
            {t("maison.rdvText")}
          </p>
          <div className="mt-10 space-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-argile">
            <p data-testid="rdv-phone-1">+225 07 00 00 02 12</p>
            <p data-testid="rdv-phone-2">+225 07 88 43 84 33</p>
            <p data-testid="rdv-phone-3">+225 07 09 73 76 12</p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <form data-testid="rdv-form" onSubmit={submit} className="gold-frame space-y-5 border border-ligne bg-card2 p-8">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">{t("maison.fName")}</label>
                <input data-testid="rdv-input-name" required value={form.full_name} onChange={set("full_name")} className="w-full border border-ligne bg-elevated px-4 py-3.5 text-sm text-ivoire focus:border-or" />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">{t("maison.fEmail")}</label>
                <input data-testid="rdv-input-email" required type="email" value={form.email} onChange={set("email")} className="w-full border border-ligne bg-elevated px-4 py-3.5 text-sm text-ivoire focus:border-or" />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">{t("maison.fPhone")}</label>
                <input data-testid="rdv-input-phone" required type="tel" value={form.phone} onChange={set("phone")} className="w-full border border-ligne bg-elevated px-4 py-3.5 text-sm text-ivoire focus:border-or" />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">{t("maison.fService")}</label>
                <select data-testid="rdv-select-service" value={form.service} onChange={set("service")} className="w-full border border-ligne bg-elevated px-4 py-3.5 text-sm text-ivoire focus:border-or">
                  {SERVICES.map((s) => <option key={s.v} value={s.v}>{t(s.labelKey)}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">{t("maison.fDate")}</label>
              <input data-testid="rdv-input-date" required type="date" value={form.preferred_date} onChange={set("preferred_date")} className="w-full border border-ligne bg-elevated px-4 py-3.5 text-sm text-ivoire focus:border-or [color-scheme:dark]" />
            </div>
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">{t("maison.fProject")}</label>
              <textarea data-testid="rdv-input-message" rows="3" value={form.message} onChange={set("message")} className="w-full border border-ligne bg-elevated px-4 py-3.5 text-sm text-ivoire focus:border-or" />
            </div>
            <button
              data-testid="rdv-submit-button"
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-3 bg-or py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-all hover:bg-orclair disabled:opacity-50"
            >
              <CalendarCheck size={15} /> {submitting ? t("maison.sending") : t("maison.submit")}
            </button>
          </form>
        </Reveal>
      </section>
    </main>
  );
}
